import { useState, useEffect, useMemo, FormEvent, ChangeEvent } from 'react';
import { Project, PartnershipLead, BilingualText, Language } from '../types';
import Card from './ui/Card';
import Button from './ui/Button';

interface PartnershipPageProps {
  project: Project;
  onClose: () => void;
  onAddPartnershipLead: (lead: Omit<PartnershipLead, 'id' | 'date'>) => void;
  language: Language;
  t: any;
}

const countries: { name: BilingualText; code: string; prefix: string; flag: string; isEurope: boolean; }[] = [
  // Europe (Prioritized)
  { name: { es: 'España', en: 'Spain' }, code: 'ES', prefix: '+34', flag: '🇪🇸', isEurope: true },
  { name: { es: 'Andorra', en: 'Andorra' }, code: 'AD', prefix: '+376', flag: '🇦🇩', isEurope: true },
  { name: { es: 'Reino Unido', en: 'United Kingdom' }, code: 'GB', prefix: '+44', flag: '🇬🇧', isEurope: true },
  { name: { es: 'Francia', en: 'France' }, code: 'FR', prefix: '+33', flag: '🇫🇷', isEurope: true },
  { name: { es: 'Alemania', en: 'Germany' }, code: 'DE', prefix: '+49', flag: '🇩🇪', isEurope: true },
  { name: { es: 'Italia', en: 'Italy' }, code: 'IT', prefix: '+39', flag: '🇮🇹', isEurope: true },
  { name: { es: 'Portugal', en: 'Portugal' }, code: 'PT', prefix: '+351', flag: '🇵🇹', isEurope: true },
  { name: { es: 'Irlanda', en: 'Ireland' }, code: 'IE', prefix: '+353', flag: '🇮🇪', isEurope: true },
  { name: { es: 'Países Bajos', en: 'Netherlands' }, code: 'NL', prefix: '+31', flag: '🇳🇱', isEurope: true },
  { name: { es: 'Bélgica', en: 'Belgium' }, code: 'BE', prefix: '+32', flag: '🇧🇪', isEurope: true },
  { name: { es: 'Suiza', en: 'Switzerland' }, code: 'CH', prefix: '+41', flag: '🇨🇭', isEurope: true },
  { name: { es: 'Austria', en: 'Austria' }, code: 'AT', prefix: '+43', flag: '🇦🇹', isEurope: true },
  { name: { es: 'Suecia', en: 'Sweden' }, code: 'SE', prefix: '+46', flag: '🇸🇪', isEurope: true },
  { name: { es: 'Noruega', en: 'Norway' }, code: 'NO', prefix: '+47', flag: '🇳🇴', isEurope: true },
  { name: { es: 'Dinamarca', en: 'Denmark' }, code: 'DK', prefix: '+45', flag: '🇩🇰', isEurope: true },
  { name: { es: 'Finlandia', en: 'Finland' }, code: 'FI', prefix: '+358', flag: '🇫🇮', isEurope: true },
  // Non-Europe
  { name: { es: 'Estados Unidos', en: 'United States' }, code: 'US', prefix: '+1', flag: '🇺🇸', isEurope: false },
  { name: { es: 'Canadá', en: 'Canada' }, code: 'CA', prefix: '+1', flag: '🇨🇦', isEurope: false },
  { name: { es: 'Argentina', en: 'Argentina' }, code: 'AR', prefix: '+54', flag: '🇦🇷', isEurope: false },
  { name: { es: 'Brasil', en: 'Brazil' }, code: 'BR', prefix: '+55', flag: '🇧🇷', isEurope: false },
  { name: { es: 'Chile', en: 'Chile' }, code: 'CL', prefix: '+56', flag: '🇨🇱', isEurope: false },
  { name: { es: 'Colombia', en: 'Colombia' }, code: 'CO', prefix: '+57', flag: '🇨🇴', isEurope: false },
  { name: { es: 'México', en: 'Mexico' }, code: 'MX', prefix: '+52', flag: '🇲🇽', isEurope: false },
  { name: { es: 'Australia', en: 'Australia' }, code: 'AU', prefix: '+61', flag: '🇦🇺', isEurope: false },
  { name: { es: 'Nueva Zelanda', en: 'New Zealand' }, code: 'NZ', prefix: '+64', flag: '🇳🇿', isEurope: false },
  { name: { es: 'Japón', en: 'Japan' }, code: 'JP', prefix: '+81', flag: '🇯🇵', isEurope: false },
  { name: { es: 'China', en: 'China' }, code: 'CN', prefix: '+86', flag: '🇨🇳', isEurope: false },
  { name: { es: 'India', en: 'India' }, code: 'IN', prefix: '+91', flag: '🇮🇳', isEurope: false },
  { name: { es: 'Emiratos Árabes Unidos', en: 'United Arab Emirates' }, code: 'AE', prefix: '+971', flag: '🇦🇪', isEurope: false },
];

type SortByType = 'name' | 'code' | 'prefix';

const PartnershipPage = ({ project, onClose, onAddPartnershipLead, language, t }: PartnershipPageProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [securityQuestion, setSecurityQuestion] = useState({ num1: 1, num2: 1 });
  const [sortBy, setSortBy] = useState<SortByType>('name');

  const sortedCountries = useMemo(() => {
    const europeanCountries = countries.filter(c => c.isEurope);
    const otherCountries = countries.filter(c => !c.isEurope);

    const sortFunction = (a: typeof countries[0], b: typeof countries[0]) => {
        if (sortBy === 'name') {
            const aName = a.name[language] || a.name['es'];
            const bName = b.name[language] || b.name['es'];
            return aName.localeCompare(bName);
        }
        if (sortBy === 'code') {
            return a.code.localeCompare(b.code);
        }
        if (sortBy === 'prefix') {
            return parseInt(a.prefix.substring(1)) - parseInt(b.prefix.substring(1));
        }
        return 0;
    };

    europeanCountries.sort(sortFunction);
    otherCountries.sort(sortFunction);

    return [...europeanCountries, ...otherCountries];
  }, [sortBy, language]);
  
  const [selectedCountry, setSelectedCountry] = useState(sortedCountries[0]);

  useEffect(() => {
    // Reset selected country if the sorted list changes to avoid mismatches
    setSelectedCountry(sortedCountries[0]);
  }, [sortedCountries]);

  useEffect(() => {
    setSecurityQuestion({
      num1: Math.floor(Math.random() * 10) + 1,
      num2: Math.floor(Math.random() * 10) + 1,
    });
  }, []);
  
  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const country = sortedCountries.find(c => c.code === e.target.value);
    if (country) {
      setSelectedCountry(country);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const securityAnswer = parseInt(formData.get('securityAnswer') as string, 10);
    const ndaAccepted = formData.get('ndaAccepted') === 'on';

    if (securityAnswer !== (securityQuestion.num1 + securityQuestion.num2)) {
      alert(t.alertIncorrectAnswer);
      return;
    }

    if (!ndaAccepted) {
      alert(t.alertNdaRequired);
      return;
    }

    const leadData: Omit<PartnershipLead, 'id' | 'date'> = {
      projectName: project.name,
      collaborationType: formData.get('collaborationType') as string,
      companyName: formData.get('companyName') as string,
      contactPerson: formData.get('contactPerson') as string,
      email: formData.get('email') as string,
      country: selectedCountry.name[language] || selectedCountry.name['es'],
      phone: `${selectedCountry.prefix} ${formData.get('phoneNumber')}`,
      website: formData.get('website') as string,
      companyDescription: formData.get('companyDescription') as string,
      detailedProposal: formData.get('detailedProposal') as string,
      estimatedBudget: formData.get('estimatedBudget') as string,
      ndaAccepted: ndaAccepted,
    };
    onAddPartnershipLead(leadData);
    setSubmitted(true);
  };
  
  const formInputClass = "w-full bg-slate-800 text-white p-3 rounded-lg border border-slate-600 focus:ring-2 focus:ring-yellow-500 focus:outline-none placeholder-slate-400";
  const formLabelClass = "block text-sm font-medium text-slate-300 mb-1.5";
  
  return (
    <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-lg z-50 p-4 animate-fade-in overflow-y-auto">
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        .form-select-arrow {
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
            background-position: right 0.5rem center;
            background-repeat: no-repeat;
            background-size: 1.5em 1.5em;
        }
      `}</style>
      <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-white transition-colors text-4xl z-10">&times;</button>
      
      <div className="w-full max-w-6xl mx-auto my-12">
        <div className="w-full max-w-4xl mx-auto text-slate-300 mb-12 px-4">
          <h1 className="text-4xl md:text-5xl font-black text-white text-center mb-6">{t.title}</h1>
          <p className="text-lg text-slate-300 mb-6">{t.intro}</p>
          <h2 className="text-2xl font-bold text-white mb-4">{t.whatWeOfferTitle}</h2>
          <ul className="list-disc list-inside space-y-2 mb-6 text-slate-300">
            <li>{t.offer1}</li>
            <li>{t.offer2}</li>
            <li>{t.offer3}</li>
          </ul>
          <p className="mb-8 italic text-slate-400">{t.outro}</p>
          <div className="space-y-6 mb-8 text-slate-300">
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature1Title}</h3><p className="mt-1">{t.feature1Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature2Title}</h3><p className="mt-1">{t.feature2Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature3Title}</h3><p className="mt-1">{t.feature3Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature4Title}</h3><p className="mt-1">{t.feature4Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature5Title}</h3><p className="mt-1">{t.feature5Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature6Title}</h3><p className="mt-1">{t.feature6Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature7Title}</h3><p className="mt-1">{t.feature7Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature8Title}</h3><p className="mt-1">{t.feature8Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature9Title}</h3><p className="mt-1">{t.feature9Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature10Title}</h3><p className="mt-1">{t.feature10Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature11Title}</h3><p className="mt-1">{t.feature11Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature12Title}</h3><p className="mt-1">{t.feature12Text}</p></div>
            <div><h3 className="text-xl font-bold text-slate-100">{t.feature13Title}</h3><p className="mt-1">{t.feature13Text}</p></div>
          </div>
          <p className="text-lg bg-slate-800/50 p-4 rounded-lg border border-slate-700">{t.callToAction}</p>
        </div>
        
        <Card className="p-8 md:p-12 bg-slate-800/60 border-slate-700">
          {submitted ? (
            <div className="text-center py-10">
              <h3 className="text-2xl font-bold text-yellow-400">{t.submittedTitle}</h3>
              <p className="mt-2 text-slate-300">{t.submittedText}</p>
              <p className="mt-1 text-slate-400">{t.submittedSubtext}</p>
              <Button variant="ghost" onClick={onClose} className="mt-6 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10">{t.backButton}</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h3 className="text-2xl font-bold text-white mb-8 text-center">{t.formTitle}</h3>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="projectName" className={formLabelClass}>{t.projectInterestLabel}</label>
                        <input id="projectName" name="projectName" type="text" value={project.name} readOnly className={`${formInputClass} bg-slate-700 cursor-not-allowed`} />
                    </div>
                    <div>
                        <label htmlFor="collaborationType" className={formLabelClass}>{t.collaborationTypeLabel}</label>
                        <select id="collaborationType" name="collaborationType" required className={`${formInputClass} appearance-none form-select-arrow`}>
                           <option value="">{t.collaborationTypePlaceholder}</option>
                           <option value="Inversión Financiera">{t.collabOptionFinancial}</option>
                           <option value="Tecnológica">{t.collabOptionTech}</option>
                           <option value="Marketing y Ventas">{t.collabOptionMarketing}</option>
                           <option value="Alianza Estratégica">{t.collabOptionStrategic}</option>
                           <option value="Investigación y Desarrollo">{t.collabOptionRnD}</option>
                           <option value="Otra">{t.collabOptionOther}</option>
                        </select>
                   </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="companyName" className={formLabelClass}>{t.companyNameLabel}</label>
                        <input id="companyName" name="companyName" type="text" placeholder={t.companyNamePlaceholder} required className={formInputClass}/>
                    </div>
                    <div>
                        <label htmlFor="contactPerson" className={formLabelClass}>{t.contactPersonLabel}</label>
                        <input id="contactPerson" name="contactPerson" type="text" placeholder={t.contactPersonPlaceholder} required className={formInputClass}/>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="email" className={formLabelClass}>{t.emailLabel}</label>
                        <input id="email" name="email" type="email" placeholder={t.emailPlaceholder} required className={formInputClass}/>
                    </div>
                    <div>
                        <div className="flex justify-between items-center mb-1.5">
                            <label htmlFor="country" className={formLabelClass + ' mb-0'}>{t.countryLabel}</label>
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                <span className="text-slate-400">{t.sortBy}</span>
                                {(['name', 'code', 'prefix'] as SortByType[]).map(type => (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => setSortBy(type)}
                                        className={`px-2 py-0.5 rounded-md transition-colors ${sortBy === type ? 'bg-yellow-500/20 text-yellow-300' : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50'}`}
                                    >
                                      { {name: t.sortByName, code: t.sortByCode, prefix: t.sortByPrefix}[type] }
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="relative">
                           <select id="country" name="country" value={selectedCountry.code} onChange={handleCountryChange} required className={`${formInputClass} appearance-none form-select-arrow`}>
                               {sortedCountries.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name[language]}</option>)}
                           </select>
                        </div>
                    </div>
                </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="phoneNumber" className={formLabelClass}>{t.phoneLabel}</label>
                        <div className="flex">
                            <span className="inline-flex items-center px-3 rounded-l-lg bg-slate-700 border border-r-0 border-slate-600 text-slate-300">
                                {selectedCountry.flag} {selectedCountry.prefix}
                            </span>
                            <input id="phoneNumber" name="phoneNumber" type="tel" placeholder={t.phonePlaceholder} className={`${formInputClass} rounded-l-none`} />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="website" className={formLabelClass}>{t.websiteLabel}</label>
                        <input id="website" name="website" type="url" placeholder={t.websitePlaceholder} className={formInputClass}/>
                    </div>
                 </div>

                <div>
                    <label htmlFor="companyDescription" className={formLabelClass}>{t.companyDescLabel}</label>
                    <textarea id="companyDescription" name="companyDescription" placeholder={t.companyDescPlaceholder} required rows={3} className={formInputClass}></textarea>
                </div>
                
                <div>
                    <label htmlFor="detailedProposal" className={formLabelClass}>{t.proposalLabel}</label>
                    <textarea id="detailedProposal" name="detailedProposal" placeholder={t.proposalPlaceholder} required rows={5} className={formInputClass}></textarea>
                </div>
                
                <div>
                    <label htmlFor="estimatedBudget" className={formLabelClass}>{t.budgetLabel}</label>
                    <input id="estimatedBudget" name="estimatedBudget" type="text" placeholder={t.budgetPlaceholder} className={formInputClass}/>
                </div>

                <div className="pt-4 border-t border-slate-700/50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                        <div>
                            <label htmlFor="securityAnswer" className={formLabelClass}>{t.securityQuestionLabel}</label>
                            <div className="flex flex-wrap items-center gap-4">
                               <span className="text-slate-300 whitespace-nowrap">{t.securityQuestionText.replace('{num1}', securityQuestion.num1.toString()).replace('{num2}', securityQuestion.num2.toString())}</span>
                               <input id="securityAnswer" name="securityAnswer" type="number" placeholder={t.securityAnswerPlaceholder} required className={`${formInputClass} w-24 sm:w-32`} />
                            </div>
                        </div>
                        <div className="self-end">
                            <label className="flex items-center text-slate-400">
                                <input name="ndaAccepted" type="checkbox" required className="h-4 w-4 rounded border-slate-500 bg-slate-700 text-yellow-500 focus:ring-yellow-500" />
                                <span className="ml-2 text-sm">{t.ndaLabel}</span>
                            </label>
                        </div>
                    </div>
                </div>

              </div>
              <div className="text-center mt-8">
                <Button type="submit" variant="secondary" className="bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-500 text-slate-900 w-full md:w-auto">
                    {t.submitButton}
                </Button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PartnershipPage;